import { OrderSide } from '@shared-types';

/**
 * The Kala API serializes its enums by **name**, not by number, so every enum
 * below is a string union of the names rather than a numeric enum. The numeric
 * values are kept in the doc comments because the exchange still reports them
 * that way elsewhere (e.g. the app's own `OrderSide`).
 */

/** `Cross` = 50, `Buy` = 65, `Sell` = 86, `None` = -1. */
export type OrderSideName = 'Cross' | 'Buy' | 'Sell' | 'None';

/**
 * `Good_Till_Cancelled` = 1, `Good_Till_Date` = 2, `Session` = 3,
 * `Fill_Or_Kill` = 69, `Immediate_Or_Cancel` = 70, `Day` = 74.
 */
export type OrderValidityName =
	| 'Good_Till_Cancelled'
	| 'Good_Till_Date'
	| 'Session'
	| 'Fill_Or_Kill'
	| 'Immediate_Or_Cancel'
	| 'Day';

/**
 * `None` = 0, `Web` = 2, `easyTrader` = 3, `Mobile` = 4, `Metatrader` = 14,
 * `ConditionalOrder` = 15, `Sama` = 18, `OnlinePlus` = 20, `BrokerApi` = 22,
 * `TestSystem` = 24.
 */
export type OrderSourceName =
	| 'None'
	| 'Web'
	/** Reported by `/orders`; the exchange doesn't document a number for it. */
	| 'Api'
	| 'easyTrader'
	| 'Mobile'
	| 'Metatrader'
	| 'ConditionalOrder'
	| 'Sama'
	| 'OnlinePlus'
	| 'BrokerApi'
	| 'TestSystem';

/** Every order this app sends originates from the Kala mobile client. */
export const ORDER_SOURCE: OrderSourceName = 'easyTrader';

/**
 * The form has no validity picker yet, so every order is sent as a day order.
 * TODO: replace with the user's choice once a validity field exists in the form.
 */
export const DEFAULT_ORDER_VALIDITY: OrderValidityName = 'Day';

export interface SubmitOrderCommand {
	symbolIsin: string;
	side: OrderSideName;
	price: number;
	volume: number;
	validity: OrderValidityName;
	/** Only meaningful for `Good_Till_Date`; null otherwise. */
	validityDate: string | null;
	source: OrderSourceName;
}

/** The order id travels in the route, not the body. */
export interface ModifyOrderRequest {
	price: number;
	volume: number;
	validity: OrderValidityName;
	validityDate: string | null;
	source: OrderSourceName;
}

/**
 * One entry per exchange order created. A submit can partially succeed, so the
 * per-entry `isAccepted` must be checked even when the envelope reports success.
 */
export interface SubmitOrderResponse {
	orderId: number;
	isAccepted: boolean;
	message: string | null;
}

export interface ModifyOrderResponse {
	orderId: number;
}

export interface CancelOrderResponse {
	orderId: number;
}

export interface MultipleCancelOrderResponse {
	orderId: number;
	isAccepted: boolean;
	message: string;
}

const ORDER_SIDE_NAMES: Record<OrderSide, OrderSideName> = {
	[OrderSide.Buy]: 'Buy',
	[OrderSide.Sell]: 'Sell',
	[OrderSide.None]: 'None'
};

export function toOrderSideName(side: OrderSide): OrderSideName {
	return ORDER_SIDE_NAMES[side] ?? 'None';
}
